"use client";

import * as React from "react";
import { useTranslations } from "next-intl";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
  DrawerOverlay,
  DrawerPortal,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { CloseIcon } from "@/components/icons";
import { cn } from "@/src/lib/utils";

export const DrawerRight = React.forwardRef<
  HTMLDivElement,
  {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    description?: string;
    className?: string;
    contentClassName?: string;
    size?: "sm" | "md" | "lg" | "xl" | "full";
    hideHeader?: boolean;
    hideFooter?: boolean;
    /**
     * If true: closes on overlay click / escape (default).
     * If false: overlay is still shown (if enabled), but outside click / escape won't close.
     */
    dismissible?: boolean;
    /**
     * Hides built-in close buttons (header X and the floating left-side close icon),
     * so the drawer can only be closed via custom controls (e.g. footer buttons).
     */
    hideCloseButton?: boolean;
    /**
     * Hides only the floating close button (left side), but keeps header close button visible
     */
    hideFloatingCloseButton?: boolean;
    mode?: "panel" | "sheet";
    headerActions?: React.ReactNode;
    overlay?: boolean | "transparent" | "none";
    overlayClassName?: string;
    zIndex?: number;
    modal?: boolean;
  }
>(
  (
    {
      open,
      onOpenChange,
      title,
      children,
      footer,
      className,
      contentClassName,
      description,
      size = "md",
      hideHeader = false,
      hideFooter = false,
      dismissible,
      hideCloseButton = false,
      hideFloatingCloseButton = false,
      mode = "panel",
      headerActions,
      overlay = true,
      overlayClassName,
      zIndex = 50,
      modal,
    }: {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      title?: React.ReactNode;
      children: React.ReactNode;
      footer?: React.ReactNode;
      description?: string;
      className?: string;
      contentClassName?: string;
      size?: "sm" | "md" | "lg" | "xl" | "full";
      hideHeader?: boolean;
      hideFooter?: boolean;
      dismissible?: boolean;
      hideCloseButton?: boolean;
      hideFloatingCloseButton?: boolean;
      mode?: "panel" | "sheet";
      headerActions?: React.ReactNode;
      overlay?: boolean | "transparent" | "none";
      overlayClassName?: string;
      zIndex?: number;
      modal?: boolean;
    },
    ref
  ) => {
    const t = useTranslations();
    const sizeClasses = {
      sm: "min-w-[360px] max-w-[400px]",
      md: "min-w-[480px] max-w-[560px]",
      lg: "min-w-[640px] max-w-[720px]",
      xl: "min-w-[860px] max-w-[960px]",
      full: "w-screen max-w-none",
    }[size];

    React.useEffect(() => {
      if (open && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, [open]);

    return (
      <Drawer
        direction="right"
        open={open}
        onOpenChange={onOpenChange}
        dismissible={dismissible ?? overlay !== "none"}
        modal={modal ?? true}
        handleOnly
      >
        {open && overlay !== false && overlay !== "none" && (modal ?? true) && (
          <DrawerPortal>
            <DrawerOverlay
              className={cn(
                overlay === "transparent" ? "bg-black/30" : "bg-black/50",
                overlayClassName
              )}
              style={{ zIndex: zIndex - 10 }}
            />
          </DrawerPortal>
        )}

        <DrawerContent
          ref={ref}
          hideInnerOverlay
          className={cn(
            "fixed right-0 top-0 h-full bg-background border-l border-gray-800 shadow-2xl flex flex-col transition-all duration-300",
            sizeClasses,
            mode === "sheet" && "rounded-l-xl",
            className
          )}
          style={{ zIndex }}
        >
          <DrawerDescription className="sr-only">
            {description || (typeof title === "string" ? title : t("label-panel"))}
          </DrawerDescription>
          
          {!hideCloseButton && !hideFloatingCloseButton && (
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="absolute -left-24 top-1/2 -translate-y-1/2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 z-[60]"
            >
              <CloseIcon size={40} strokeWidth={0.75}/>
            </button>
          )}
          {!hideHeader && (
            <DrawerHeader className="flex flex-row items-center justify-between gap-2 px-5 py-3 border-b !bg-[rgba(229,229,229,1)] sticky top-0 z-[100]">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {!hideCloseButton && (
                  <DrawerClose asChild>
                    <Button
                      onClick={() => onOpenChange(false)}
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                    >
                      <X className="h-5 w-5" />
                      <span className="sr-only">{t("btn-close")}</span>
                    </Button>
                  </DrawerClose>
                )}

                <div className="flex min-w-0 flex-1 flex-col">
                  <DrawerTitle className="text-base font-semibold truncate">
                    {(() => {
                      const resolvedTitle =
                        React.isValidElement<{ content?: React.ReactNode }>(title) &&
                        title.props?.content != null
                          ? title.props.content
                          : title;

                      return resolvedTitle || t("label-panel");
                    })()}
                  </DrawerTitle>
                  {description && (
                    <p className="text-sm text-muted-foreground truncate">
                      {description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">{headerActions}</div>
            </DrawerHeader>
          )}

          <div
            className={cn(
              "flex-1 overflow-y-auto px-5 py-4 flex flex-col",
              mode === "sheet" && "px-6 py-5",
              contentClassName
            )}
          >
            {children}
          </div>

          {!hideFooter && footer && (
            <div
              className={cn(
                "border-t p-3 flex items-center justify-end gap-2 bg-muted/40 sticky bottom-0 z-10",
                mode === "sheet" && "bg-background/90 backdrop-blur-md"
              )}
            >
              {footer}
            </div>
          )}
        </DrawerContent>
      </Drawer>
    );
  }
);
DrawerRight.displayName = "DrawerRight";
